import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { PageSEO } from '@/components/SEO';
import { MicroLabel } from '@/components/brand';
import api from '@/services/api';
import { Eye, EyeOff, Loader2, CheckCircle, RefreshCw } from 'lucide-react';
import { Logo } from '@/components/ui/Logo';

export default function Register() {
  const navigate = useNavigate();
  
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  
  // Resend email state
  const [resendCount, setResendCount] = useState(0);
  const [isResending, setIsResending] = useState(false);
  const [resendMessage, setResendMessage] = useState<string | null>(null);
  const [resendError, setResendError] = useState<string | null>(null);

  const validatePassword = (pass: string) => {
    if (pass.length < 8) return 'Password must be at least 8 characters';
    return null;
  };

  const handleResendEmail = async () => {
    if (resendCount >= 2) {
      setResendError('Email verification is not working right now. Please try again later.');
      setResendMessage(null);
      return;
    }

    setIsResending(true);
    setResendMessage(null);
    setResendError(null);

    try {
      await api.resendVerification(email);
      setResendCount(prev => prev + 1);
      setResendMessage('Verification email sent! Please check your inbox.');
    } catch (err: unknown) {
      if (err && typeof err === 'object' && 'response' in err) {
        const axiosError = err as { response?: { data?: { detail?: string } } };
        setResendError(axiosError.response?.data?.detail || 'Failed to resend email. Please try again.');
      } else {
        setResendError('Failed to resend email. Please try again.');
      }
    } finally {
      setIsResending(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!fullName.trim()) {
      setError('Full name is required');
      return;
    }

    const passwordError = validatePassword(password);
    if (passwordError) {
      setError(passwordError);
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setIsLoading(true);

    try {
      await api.register({
        email,
        password,
        full_name: fullName.trim(),
        accepted_terms: acceptedTerms,
      });
      setSuccess(true);
    } catch (err: unknown) {
      if (err && typeof err === 'object' && 'response' in err) {
        const axiosError = err as { response?: { data?: { detail?: string } } };
        setError(axiosError.response?.data?.detail || 'Registration failed. Please try again.');
      } else {
        setError('Registration failed. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    if (!acceptedTerms) {
      setError('Please accept the Terms of Service and Privacy Policy before continuing.');
      return;
    }
    try {
      const { authorization_url } = await api.googleAuthUrl();
      window.location.href = authorization_url;
    } catch {
      setError('Failed to initialize Google signup.');
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-paper dark:bg-dark-bg flex flex-col items-center justify-center px-6 transition-colors">
        <PageSEO.Register />
        <div className="relative z-10 w-full max-w-md mx-auto animate-fade-in text-center">
          <div className="mb-6">
            <CheckCircle className="w-16 h-16 text-success-text dark:text-green-400 mx-auto" />
          </div>
          <h2 className="font-display text-ink dark:text-dark-text text-xl sm:text-2xl mb-4 leading-snug">
            Check your email
          </h2>
          <p className="text-steel-dark dark:text-dark-muted mb-6">
            We've sent a verification link to <strong className="text-ink dark:text-dark-text">{email}</strong>. 
            Please check your inbox and click the link to verify your account.
          </p>
          
          {resendMessage && (
            <div className="mb-4 p-3 rounded-2xl bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 text-green-700 dark:text-green-400 text-sm">
              {resendMessage}
            </div>
          )}
          
          {resendError && (
            <div className="mb-4 p-3 rounded-2xl bg-error-bg dark:bg-red-900/20 border border-error-border dark:border-red-800 text-error-text dark:text-red-400 text-sm">
              {resendError}
            </div>
          )}
          
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button onClick={() => navigate('/login')} variant="outline" className="dark:border-dark-border dark:text-dark-text dark:hover:bg-dark-surface">
              Back to Login
            </Button>
            <Button 
              onClick={handleResendEmail} 
              variant="ghost"
              disabled={isResending || resendCount >= 2}
              className="text-ink dark:text-dark-text"
            >
              {isResending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <RefreshCw className="mr-2 h-4 w-4" />
                  Resend Email
                </>
              )}
            </Button>
          </div>
          
          {resendCount > 0 && resendCount < 2 && (
            <p className="mt-3 text-xs text-steel-dark dark:text-dark-muted">
              {2 - resendCount} resend attempt{2 - resendCount !== 1 ? 's' : ''} remaining
            </p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-paper dark:bg-dark-bg flex flex-col items-center justify-center px-6 py-12 relative overflow-hidden transition-colors">
      <PageSEO.Register />

      <div className="relative z-10 w-full max-w-md mx-auto animate-fade-in">
        <div className="flex flex-col items-center mb-8">
          <Link to="/">
            <Logo variant="icon-wordmark" className="h-12" />
          </Link>
          <p className="font-body font-light text-ink dark:text-dark-text text-xl sm:text-2xl">
            Turbocharge Your Communication to Your Target Audience.
          </p>
        </div>

        <div className="bg-white dark:bg-dark-surface rounded-lg border border-hairline dark:border-dark-border p-8 shadow-subtle transition-colors">
          <h2 className="font-display text-ink dark:text-dark-text text-xl sm:text-2xl mb-6 text-center leading-snug">
            Create your account
          </h2>

          {error && (
            <div className="mb-4 p-3 rounded-2xl bg-error-bg dark:bg-red-900/20 border border-error-border dark:border-red-800 text-error-text dark:text-red-400 text-sm">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <MicroLabel as="label" htmlFor="fullName">Full Name</MicroLabel>
              <Input
                id="fullName"
                type="text"
                placeholder="John Doe"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                required
                autoComplete="name"
                className="dark:bg-dark-bg dark:border-dark-border dark:text-dark-text dark:placeholder:text-dark-muted"
              />
            </div>

            <div className="space-y-2">
              <MicroLabel as="label" htmlFor="email">Email</MicroLabel>
              <Input
                id="email"
                type="email"
                placeholder="you@company.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
                className="dark:bg-dark-bg dark:border-dark-border dark:text-dark-text dark:placeholder:text-dark-muted"
              />
            </div>

            <div className="space-y-2">
              <MicroLabel as="label" htmlFor="password">Password</MicroLabel>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoComplete="new-password"
                  className="pr-10 dark:bg-dark-bg dark:border-dark-border dark:text-dark-text dark:placeholder:text-dark-muted"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-steel-dark dark:text-dark-muted hover:text-ink dark:hover:text-dark-text transition-colors"
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
              <p className="text-xs text-steel-dark dark:text-dark-muted">At least 8 characters</p>
            </div>

            <div className="space-y-2">
              <MicroLabel as="label" htmlFor="confirmPassword">Confirm Password</MicroLabel>
              <Input
                id="confirmPassword"
                type={showPassword ? 'text' : 'password'}
                placeholder="••••••••"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                autoComplete="new-password"
                className="dark:bg-dark-bg dark:border-dark-border dark:text-dark-text dark:placeholder:text-dark-muted"
              />
            </div>

            <div className="flex items-start gap-2 mt-2">
              <input
                type="checkbox"
                id="acceptTerms"
                checked={acceptedTerms}
                onChange={(e) => {
                  setAcceptedTerms(e.target.checked);
                  // Clear the error the moment its cause is gone. `error` was only reset
                  // inside handleSubmit, so "Please accept the Terms of Service" stayed on
                  // screen in red after the user had accepted them — the banner outlived
                  // the condition it described.
                  if (e.target.checked) setError(null);
                }}
                className="mt-1 h-4 w-4 rounded border-hairline dark:border-dark-border"
              />
              <label htmlFor="acceptTerms" className="text-sm text-steel-dark dark:text-dark-muted">
                I agree to the{' '}
                <a href="/terms" target="_blank" className="text-ink hover:underline">Terms of Service</a>
                {' '}and{' '}
                <a href="/privacy" target="_blank" className="text-ink hover:underline">Privacy Policy</a>
              </label>
            </div>

            <Button type="submit" className="w-full bg-cargo hover:bg-cargo/90 text-cargo-ink rounded-md focus-visible:ring-ink" size="lg" disabled={isLoading || !acceptedTerms}>
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Creating account...
                </>
              ) : (
                'Create Account'
              )}
            </Button>
          </form>

          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-hairline dark:border-dark-border" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-4 bg-white dark:bg-dark-surface text-steel-dark dark:text-dark-muted">or</span>
            </div>
          </div>

          <Button
            type="button"
            variant="outline"
            className="w-full dark:border-dark-border dark:text-dark-text dark:hover:bg-dark-bg"
            size="lg"
            onClick={handleGoogleLogin}
          >
            <svg className="mr-2 h-5 w-5" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
            </svg>
            Continue with Google
          </Button>

          <p className="mt-6 text-center text-sm text-steel-dark dark:text-dark-muted">
            Already have an account?{' '}
            <Link to="/login" className="text-ink dark:text-dark-text font-medium hover:underline">
              Log in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
